import { z } from "zod";
import { ISODateSchema, ULIDSchema } from "./primitives.js";
// ----- Safe-string transform: strips HTML, control chars; length-caps -----
// Used everywhere LLM-derived strings land. Allows \n (0x0A) and \t (0x09).
export function safeString(maxLen) {
    return z
        .string()
        .transform((s) => s
        .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, "") // strip script/style blocks fully
        .replace(/<[^>]*>/g, "") // strip remaining HTML tags
        // eslint-disable-next-line no-control-regex -- LLM-injection defense: strip control chars (keep \n \t)
        .replace(/[\x00-\x08\x0B-\x1F\x7F]/g, "")
        .trim()
        .slice(0, maxLen));
}
// ----- Non-empty safe-string: same strip/cap as safeString, but rejects
//       anything that is empty after stripping (e.g. "<i></i>" → "").
//       Used for fields RecipeRowSchema requires non-empty: title, ingredient
//       name, step text. Prevents a poison row that makes rowToApiItem throw. -----
export function nonEmptySafeString(maxLen) {
    return safeString(maxLen).refine((s) => s.length > 0, {
        message: "must not be empty after stripping HTML/control chars",
    });
}
// ----- Status / kind / model enums -----
export const ImportJobStatusSchema = z.enum([
    "queued",
    "fetching",
    "extracting",
    "saving",
    "done",
    "failed",
    "over_cap",
]);
export const ImportJobKindSchema = z.enum(["url", "text", "photo"]);
export const ImportModelSchema = z.enum(["haiku", "sonnet", "sonnet5"]);
// Default model for the photo-import vision path (Slice D). Sonnet 5 (high-res)
// is the intended target, but AWS declined Sonnet 5 model access for this account
// (insufficient usage/billing history, 2026-07-05), so the photo path runs on
// Sonnet 4.6 in the interim. Flip this to "sonnet5" (one line) when access is
// granted — the alias, pricing, and worker IAM ARNs for Sonnet 5 are already in
// place. Note: 4.6 is NOT high-res (downscales to ~1568px), so fraction/
// handwriting fidelity is lower — the mandatory pending-draft review is the guard.
export const PHOTO_IMPORT_MODEL = "sonnet";
// ----- Error envelope -----
export const ImportJobErrorCodeSchema = z.enum([
    "URL_FETCH_TIMEOUT",
    "URL_FETCH_BODY_TOO_LARGE",
    "URL_FETCH_BLOCKED",
    "URL_FETCH_HTTP_ERROR",
    "UNSUPPORTED_CONTENT_TYPE",
    "LLM_INVOKE_FAILED",
    "LLM_OUTPUT_INVALID",
    "LLM_OUTPUT_INCOMPLETE",
    "RECIPE_WRITE_FAILED",
    "MONTHLY_CAP_EXCEEDED",
    "NOT_A_RECIPE",
    "UNKNOWN",
]);
export const ImportJobErrorSchema = z
    .object({
    code: z.string().min(1).max(64),
    message: z.string().min(1).max(500),
})
    .nullable();
// ----- Row schema (DDB shape; key attributes are lowercase householdId + sk
//       matching the table definition in template.yaml and every other table) -----
const BaseImportJobRowSchema = z
    .object({
    householdId: ULIDSchema("hh"),
    sk: ULIDSchema("job"),
    id: ULIDSchema("job"),
    schemaVersion: z.literal(1),
    type: z.literal("ImportJob"),
    kind: ImportJobKindSchema,
    status: ImportJobStatusSchema,
    model: ImportModelSchema,
    url: z.string().url().max(2048).nullable(),
    text: z.string().min(1).max(50_000).nullable(),
    // Photo-import inputs (kind:"photo"): the `ph_` keys of the uploaded page
    // photos (null for url/text jobs). `warnings` carries extraction uncertainty
    // — illegible/covered text, cross-recipe or duplicate-page notes — surfaced
    // in the review screen. Listed explicitly so they survive the .omit() into
    // ImportJobApiItemSchema. `extracted` is added below (it references
    // ExtractedRecipeWithTagsSchema, declared later in this file).
    photoKeys: z.array(ULIDSchema("ph")).nullable().default(null),
    warnings: z.array(safeString(200)).max(20).default([]),
    recipeId: z.string().nullable().default(null),
    error: ImportJobErrorSchema.default(null),
    tokensInput: z.number().int().nonnegative().nullable().default(null),
    tokensOutput: z.number().int().nonnegative().nullable().default(null),
    tokensCacheRead: z.number().int().nonnegative().nullable().default(null),
    tokensCacheWrite: z.number().int().nonnegative().nullable().default(null),
    costUsdCents: z.number().int().nonnegative().nullable().default(null),
    fetchedAt: ISODateSchema.nullable().default(null),
    // Link-preview metadata parsed from the source page's Open Graph tags by the
    // import worker (best-effort; null when absent or any preview step failed).
    // Each field is independently nullable so partial metadata renders gracefully.
    // Listed explicitly (not relying on .passthrough()) so it survives the
    // .omit() into ImportJobApiItemSchema and is returned by the API.
    sourceMeta: z
        .object({
        title: safeString(200).nullable(),
        description: safeString(500).nullable(),
        siteName: safeString(120).nullable(),
        imageKey: z.string().max(256).nullable(), // S3/CDN object key, e.g. previews/<jobId>.jpg
    })
        .nullable()
        .default(null),
    createdBy: z.string(),
    createdAt: ISODateSchema,
    updatedAt: ISODateSchema,
    completedAt: ISODateSchema.nullable().default(null),
    ttl: z.number().int().positive().nullable().default(null),
});
// ----- ImportJobRowSchema (with `extracted`) + API item schema -----
// Declared at the bottom of this file — `extracted` references
// ExtractedRecipeWithTagsSchema, which is defined below.
// ----- Request schemas -----
export const CreateImportRequestSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("url"),
        url: z.string().url().max(2048).refine((u) => u.startsWith("https://"), {
            message: "url must be https",
        }),
        model: ImportModelSchema.optional().default("haiku"),
    })
        .strict(),
    z
        .object({
        kind: z.literal("text"),
        text: z.string().min(20).max(50_000),
        model: ImportModelSchema.optional().default("haiku"),
    })
        .strict(),
    z
        .object({
        kind: z.literal("photo"),
        photoKeys: z.array(ULIDSchema("ph")).min(1).max(8),
        model: ImportModelSchema.optional().default(PHOTO_IMPORT_MODEL),
    })
        .strict(),
]);
// ----- LLM extraction output schema (parsed from Bedrock's response text) -----
const ExtractedIngredientSchema = z.object({
    raw: safeString(200),
    qty: z.number().positive().nullable(),
    // Cap ⊆ RecipeRow unit max(32). Was safeString(40), which let through a
    // 33–40-char unit that RecipeRowSchema then rejected → poison row.
    unit: safeString(32).nullable(),
    canonical: z
        .object({
        qty: z.number().nonnegative(),
        unit: z.enum(["g", "ml", "count", "dash"]),
        convertible: z.boolean(),
    })
        .nullable(),
    // RecipeRow requires name non-empty; reject empty-after-strip here.
    name: nonEmptySafeString(100),
    notes: safeString(200).nullable(),
});
const ExtractedStepSchema = z.object({
    kind: z.enum(["active", "prep", "wait"]),
    // RecipeRow requires step text non-empty; reject empty-after-strip here.
    text: nonEmptySafeString(2000),
    timers: z
        .array(z.object({
        label: safeString(40),
        minutes: z.number().int().positive().max(10_080),
    }))
        .max(20)
        .default([]),
    estimatedMin: z.number().int().positive().max(10_080).nullable().default(null),
    // 0-based indices of OTHER steps that can run concurrently with this one.
    // The worker maps these indices to the generated step ids and drops self /
    // out-of-range refs. Capped at 20.
    parallelWith: z.array(z.number().int().nonnegative()).max(20).default([]),
    // Image candidate numbers (`[image N]` placeholders in the body Markdown) that
    // illustrate this step. The model emits numbers, never URLs. Capped at 2/step;
    // the worker drops out-of-range refs and stores the resolved images.
    imageRefs: z.array(z.number().int().nonnegative()).max(2).default([]),
});
// activeTimeMin ≤ totalTimeMin — shared by ExtractedRecipeSchema AND
// ExtractedRecipeWithTagsSchema (the one llm-bedrock.ts actually parses with).
// Mirrors RecipeRowSchema's refine so the assembled row never violates it.
const timeRangeRefine = (r) => r.activeTimeMin == null || r.totalTimeMin == null || r.activeTimeMin <= r.totalTimeMin;
const TIME_RANGE_REFINE_OPTS = {
    message: "activeTimeMin must be ≤ totalTimeMin",
    path: ["activeTimeMin"],
};
export const ExtractedRecipeSchema = z
    .object({
    // RecipeRow requires title non-empty; reject empty-after-strip here.
    title: nonEmptySafeString(200),
    description: safeString(2048).nullable(),
    servings: z.number().int().min(1).max(999).nullable(),
    totalTimeMin: z.number().int().min(1).max(10_080).nullable(),
    activeTimeMin: z.number().int().min(1).max(10_080).nullable(),
    difficulty: z.enum(["easy", "medium", "hard"]).nullable(),
    ingredients: z.array(ExtractedIngredientSchema).min(1).max(200),
    steps: z.array(ExtractedStepSchema).min(1).max(200),
    notes: safeString(10_240).nullable(),
})
    .refine(timeRangeRefine, TIME_RANGE_REFINE_OPTS);
export const ExtractedRecipeWithTagsSchema = z
    .object({
    // Recipe core (we re-list to avoid extending+refine quirks)
    title: nonEmptySafeString(200),
    description: safeString(2048).nullable(),
    servings: z.number().int().min(1).max(999).nullable(),
    totalTimeMin: z.number().int().min(1).max(10_080).nullable(),
    activeTimeMin: z.number().int().min(1).max(10_080).nullable(),
    difficulty: z.enum(["easy", "medium", "hard"]).nullable(),
    ingredients: z.array(ExtractedIngredientSchema).min(1).max(200),
    steps: z.array(ExtractedStepSchema).min(1).max(200),
    notes: safeString(10_240).nullable(),
    // Tags
    suggestedTags: z.array(safeString(40)).max(20).default([]),
    suggestedCategories: z.array(safeString(40)).max(10).default([]),
    suggestedCuisine: safeString(40).nullable(),
    suggestedDietFlags: z
        .array(z.enum(["vegetarian", "vegan", "gluten-free", "dairy-free", "nut-free", "egg-free", "low-carb"]))
        .max(10)
        .default([]),
    // General recipe-level image candidate numbers (not tied to a single step).
    // Capped at 10; the worker drops out-of-range refs.
    recipeImageRefs: z.array(z.number().int().nonnegative()).max(10).default([]),
    // Extraction uncertainty channel — populated only by the photo path
    // (illegible/covered text, cross-recipe/duplicate-page notes). url/text
    // extraction leaves it at the [] default. Surfaced as a review banner.
    warnings: z.array(safeString(200)).max(20).default([]),
    // Telemetry
    tokensInput: z.number().int().nonnegative(),
    tokensOutput: z.number().int().nonnegative(),
    tokensCacheRead: z.number().int().nonnegative().default(0),
    tokensCacheWrite: z.number().int().nonnegative().default(0),
})
    // Same time-range refine as ExtractedRecipeSchema + RecipeRowSchema. Previously
    // missing here, so a row with activeTimeMin > totalTimeMin reached the table.
    .refine(timeRangeRefine, TIME_RANGE_REFINE_OPTS);
// ----- Row schema (with the persisted extraction result) + API item -----
// Declared here (not with the other row fields above) because `extracted`
// references ExtractedRecipeWithTagsSchema, defined just above. The photo
// worker persists the extraction JSON under a conditional extracting→extracted
// transition (idempotency); null for url/text jobs and before extraction runs.
export const ImportJobRowSchema = BaseImportJobRowSchema.extend({
    extracted: ExtractedRecipeWithTagsSchema.nullable().default(null),
}).passthrough();
// ----- API item schema (omits internal DDB key attributes) -----
export const ImportJobApiItemSchema = ImportJobRowSchema.omit({ householdId: true, sk: true });
//# sourceMappingURL=import-job.js.map